import { useState, useEffect, useCallback } from 'react';

const SCRIPT_ID = 'onlyoffice-api-script';

// Turn OFF OnlyOffice's "AutoFormat As You Type" for lists (typing "* " / "1. " no longer
// converts the line into a bullet/numbered list). OnlyOffice has no config/API toggle for
// this — it's a per-user setting persisted in localStorage on the editor's origin (same
// origin as the app in production). We seed the defaults ONCE so we don't stomp a user who
// later re-enables it via File → Advanced Settings → Proofing → AutoCorrect. Must run
// before the editor iframe initializes, hence it's called at the top of load().
const AUTOFORMAT_SEED_FLAG = 'pacs-autoformat-defaults-applied';
function seedAutoformatDefaults() {
  try {
    if (localStorage.getItem(AUTOFORMAT_SEED_FLAG)) return;
    localStorage.setItem('de-settings-autoformat-bulleted', '0');
    localStorage.setItem('de-settings-autoformat-numbered', '0');
    localStorage.setItem(AUTOFORMAT_SEED_FLAG, '1');
  } catch { /* localStorage unavailable — nothing to seed */ }
}

export default function useOnlyOfficeScript(onlyOfficeUrl) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!onlyOfficeUrl) return;

    // Disable list autoformat before the editor reads its settings
    seedAutoformatDefaults();

    // Already available on window
    if (window.DocsAPI) {
      setLoaded(true);
      return;
    }

    // Script tag already in DOM — wait for it
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => setLoaded(true));
      existing.addEventListener('error', () => setError('Failed to load OnlyOffice script'));
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `${onlyOfficeUrl}/web-apps/apps/api/documents/api.js`;
    script.async = true;
    script.onload = () => setLoaded(true);
    script.onerror = () => setError('Failed to load OnlyOffice script');
    document.head.appendChild(script);
  }, [onlyOfficeUrl]);

  useEffect(() => {
    setError(null);
    setLoaded(!!window.DocsAPI);
    load();
  }, [load]);

  return { loaded, error, retry: load };
}
